import * as React from "react";
import { Lock, LockKeyholeOpen } from "lucide-react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { type FsEntryAccess } from "@/types/fse";
import { useToastDefault } from "@/hooks/use-toast-default";
import { useUpdateFileAccessMutation } from "@/api/files";

interface Props {
  access: FsEntryAccess;
  fseId: string;
  isReadOnly: boolean;
}

interface FormInputs {
  access: FsEntryAccess;
}

export const ShareModalGeneralAccess: React.FC<Props> = ({ access, fseId, isReadOnly }) => {
  const methods = useForm<FormInputs>({
    defaultValues: {
      access,
    },
  });
  const { showToast } = useToastDefault();
  const [updateFileAccess] = useUpdateFileAccessMutation();

  const newAccess = useWatch({
    name: "access",
    control: methods.control,
  });

  const updatePermission = React.useCallback(
    async (newAccess: FsEntryAccess) => {
      await updateFileAccess({ fse_id: fseId, access: newAccess }).unwrap();
      showToast("Success", "General access change!");
    },
    [fseId, showToast, updateFileAccess],
  );

  React.useEffect(() => {
    if (access !== newAccess) {
      void updatePermission(newAccess);
    }
  }, [newAccess, access, updatePermission]);

  return (
    <div className="mt-3">
      <FormProvider {...methods}>
        <h4 className="text-sm font-semibold">General access</h4>
        <div className="flex">
          <div className="mr-2 mt-3 text-gray-500">{access === "restricted" ? <Lock /> : <LockKeyholeOpen />}</div>
          <div>
            <select
              disabled={isReadOnly}
              {...methods.register("access")}
              className="mt-2 block rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm/6"
            >
              <option value="restricted">Restricted</option>
              <option value="public">Visible for everone</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {access === "restricted"
                ? "Only people assigned can access this item"
                : "Everyone in your organization can access this item"}
            </p>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};
